'use client';

import { useRef, useEffect } from 'react';

import { Box } from '@mui/material';

/** 화면의 20%가 들어오면 재생한다 */
const PLAY_THRESHOLD = 0.2;

/**
 * 상세페이지 영상 — 판매처 원본처럼 컨트롤 없이 자동 재생·반복.
 *
 * ⚠️ 자동재생은 **`muted` + `playsInline`이 필수**다. 하나라도 빠지면 브라우저가 막는다.
 *
 * ⚠️ 상세페이지 한 장에 영상이 스무 개 가까이 붙는다. **화면에 들어온 것만 재생**하고
 * 벗어나면 멈춘다 — 전부 동시에 물면 네트워크·디코딩 부담이 크다.
 *
 * ⚠️ `play()`의 실패는 삼킨다. 사용자가 아직 페이지를 건드리지 않았으면 브라우저가
 * 거절하는데, 그건 오류가 아니라 정책이다.
 */
export default function AutoPlayVideo({ src }: { src: string }) {
  const ref = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return undefined;

    const play = () => {
      const played = element.play();
      if (played?.catch) played.catch(() => {});
    };

    if (typeof IntersectionObserver === 'undefined') {
      play();
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) play();
          else element.pause();
        });
      },
      { threshold: PLAY_THRESHOLD },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return (
    <Box
      component="video"
      ref={ref}
      src={src}
      loop
      muted
      playsInline
      preload="metadata"
      sx={{ width: '100%', display: 'block', verticalAlign: 'top', bgcolor: 'common.black' }}
    />
  );
}
